import type { AxiosResponse } from 'axios';

import api from 'api/api.config';

import { pathWithCurrentUserId } from 'helpers/api/path-with-client-id';
import { buildQueryParams } from 'helpers/api/build-query-params';
import type {
  SortDirection,
  FilterValue,
} from 'helpers/api/build-query-params';
import { pathBuilder } from 'helpers/api/path-builder';

type ApiPromise<T = unknown> = Promise<AxiosResponse<T>>;

export interface ClientDto {
  id: string | number;
  consultants?: unknown[];
  [key: string]: unknown;
}

export interface UserDto {
  id: string | number;
  oktaUserId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  status?: string;
  jobTitle?: string;
  lastLogin?: string;
  client?: ClientDto;
  authority?: string;
  reports?: UserReportDto[];
  [key: string]: unknown;
}

export interface UserReportDto {
  id: string | number;
  title?: string;
  productId?: string | number;
  powerBiId?: string;
  description?: string;
  powerBiReportType?: string;
  powerBiTitle?: string;
  hasDataIssue?: boolean;
  productName?: string;
  productType?: string;
  [key: string]: unknown;
}

export interface BaseUserDto {
  id: string | number;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  [key: string]: unknown;
}

export interface BaseAssetDto {
  fileExt?: string;
  fileName?: string;
  name?: string;
  description?: string;
  createdDate?: string;
  [key: string]: unknown;
}

export interface AssetDto extends BaseAssetDto {
  id: string | number;
  clientId?: string | number;
  status?: string;
  fileSize?: number;
  autoAssignToUsers?: boolean;
}

export interface UserAccessDto {
  userId: string | number;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  hasAccess: boolean;
  [key: string]: unknown;
}

export interface AssetClientUserUpdateRequest {
  userId: string | number;
  hasAccess: boolean;
}

export interface UserAssetDto {
  id: string | number;
  asset: BaseAssetDto;
  viewed: boolean;
  [key: string]: unknown;
}

export interface CollectionPagination {
  page: number;
  pageSize: number;
  pageCount: number;
  totalRecords: number;
  [key: string]: unknown;
}

export interface CollectionResponse<T = unknown> {
  data: T;
  pagination: CollectionPagination;
}

export interface GetCollectionOptions {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
  searchTerms?: string;
  forceSearch?: boolean;
  headers?: Record<string, string>;
}

export const getCollection = (
  type: string,
  {
    pageSize,
    pageIndex,
    sortBy,
    filters,
    searchTerms,
    forceSearch = false,
    headers,
  }: GetCollectionOptions
): Promise<CollectionResponse> => {
  const path = pathBuilder(type, filters);

  let url = pathWithCurrentUserId(path);

  if (searchTerms || forceSearch) url += '/_search';

  return api
    .get(url, {
      headers: headers,
      data: null, // Have to do this to add Content-Type header, which we pass for exports (https://github.com/axios/axios/issues/86)
      params: buildQueryParams({
        pageSize,
        pageIndex,
        sortBy,
        filters,
        searchTerms,
        forceSearch,
      }),
    })
    .then(({ data, headers }) => {
      const totalCount = headers['x-total-count'];
      const pageSize = headers['x-page-size'];
      const pageCount = headers['x-page-count'];

      const numPages = Math.ceil(
        Number.parseInt(totalCount, 10) / Number.parseInt(pageSize, 10)
      );

      return {
        data: data,
        pagination: {
          page: Number.parseInt(pageCount, 10),
          pageSize: Number.parseInt(pageSize, 10),
          pageCount: numPages,
          totalRecords: Number.parseInt(totalCount, 10),
        },
      };
    });
};

export const getCurrentUser = (): ApiPromise<UserDto> =>
  api.get(pathWithCurrentUserId('/account'));

export interface RecordWithId {
  id?: string | number;
  [key: string]: unknown;
}

export const createOrUpdate = (
  type: string,
  data: RecordWithId
): ApiPromise => {
  const apiMethod = data.id ? api.put : api.post;

  return apiMethod(pathWithCurrentUserId(`/${type}`), JSON.stringify(data));
};

// TODO rm -- we don't have delete in the real app
// `id` is typed as `unknown` to preserve the legacy slice call site that
// occasionally passes a whole record; runtime behavior is unchanged.
export const deleteRecord = (type: string, id: unknown): ApiPromise =>
  api.delete(pathWithCurrentUserId(`/${type}/${String(id)}`));

export const find = (type: string, id: unknown): ApiPromise =>
  api.get(pathWithCurrentUserId(`/${type}/${String(id)}`));

export interface ChangePasswordArgs {
  newPassword: string;
  oldPassword: string;
}

export const changePassword = ({
  newPassword,
  oldPassword,
}: ChangePasswordArgs): ApiPromise =>
  api.post(
    pathWithCurrentUserId('/users/change-password'),
    JSON.stringify({
      newPassword,
      oldPassword,
    })
  );

export const getReports = (): ApiPromise<UserReportDto[]> =>
  api.get(pathWithCurrentUserId('/users/reports'));

export const getReportToken = (reportId: string | number): ApiPromise =>
  api.post(pathWithCurrentUserId(`/powerbi/token/${reportId}`));

export interface GetClientReportTokenArgs {
  reportId: string | number;
  clientId: string | number;
}

export const getClientReportToken = ({
  reportId,
  clientId,
}: GetClientReportTokenArgs): ApiPromise =>
  api.post(
    pathWithCurrentUserId(`/powerbi/token/${reportId}`),
    JSON.stringify({ clientId })
  );

//--- Feature Flags Setting ---
export const getFeatureFlagsSetting = (oktaUserId: string): ApiPromise =>
  api.get(`/${oktaUserId}/features`);

//--- password reset ---
export const requestPasswordReset = (email: string): ApiPromise =>
  api.textpost('/account/password-reset', email);

export interface ResetPasswordArgs {
  key: string;
  newPassword: string;
}

export const resetPassword = ({
  key,
  newPassword,
}: ResetPasswordArgs): ApiPromise =>
  api.post(
    '/account/password-reset/new-password',
    JSON.stringify({
      key,
      newPassword,
    })
  );

//--- new user methods ---
export const validateActivationKey = (activationKey: string): ApiPromise =>
  api.get('/account/activate', {
    params: { key: activationKey },
  });

export interface ActivateUserAccountArgs {
  activationKey: string;
  oktaUserId: string;
  password: string;
}

//--- files manipulation methods ---
export const activateUserAccount = ({
  activationKey,
  oktaUserId,
  password,
}: ActivateUserAccountArgs): ApiPromise =>
  api.post(
    '/account/activate',
    JSON.stringify({
      key: activationKey,
      oktaUserId,
      password,
    })
  );

export interface CurrentUserHolder {
  data: {
    oktaUserId: string;
    [key: string]: unknown;
  };
}

export interface UploadFileArgs {
  fileName: { value: string };
  file: Blob | File;
  description?: string;
  currentUser: CurrentUserHolder;
  clientId: string | number;
}

export const uploadFile = ({
  fileName,
  file,
  description,
  currentUser,
  clientId,
}: UploadFileArgs): ApiPromise<AssetDto> => {
  const formData = new FormData();

  formData.append('file', file);

  formData.append(
    'asset',
    new Blob(
      [
        JSON.stringify({
          name: fileName.value,
          description,
        }),
      ],
      {
        type: 'application/json',
      }
    )
  );

  return api.post(
    `/${currentUser.data.oktaUserId}/clients/${clientId}/assets`,
    formData
  );
};

export interface DownloadFileArgs {
  currentUser: CurrentUserHolder;
  clientId?: string | number;
  fileId: string | number;
}

export const downloadFile = ({
  currentUser,
  clientId,
  fileId,
}: DownloadFileArgs): ApiPromise<Blob> => {
  const urlForDownload = !!clientId
    ? `clients/${clientId}/assets`
    : `user-assets`;

  return api.get(
    `/${currentUser.data.oktaUserId}/${urlForDownload}/${fileId}/download`,
    {
      responseType: 'blob',
    }
  );
};

export interface UpdateFileArgs {
  fileName?: { value: string };
  file?: (Blob | File) & { isFakeFile?: boolean };
  description?: string;
  currentUser: CurrentUserHolder;
  clientId: string | number;
  fileId: string | number;
  status?: string;
  autoAssignToUsers?: boolean;
}

export const updateFile = ({
  fileName,
  file,
  description,
  currentUser,
  clientId,
  fileId,
  status,
  autoAssignToUsers,
}: UpdateFileArgs): ApiPromise<AssetDto> => {
  const formData = new FormData();

  if (file && !file.isFakeFile) {
    formData.append('file', file);
  }

  formData.append(
    'asset',
    new Blob(
      [
        JSON.stringify({
          name: fileName?.value,
          description,
          status,
          autoAssignToUsers,
        }),
      ],
      {
        type: 'application/json',
      }
    )
  );

  return api.patch(
    `/${currentUser.data.oktaUserId}/clients/${clientId}/assets/${fileId}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
};

export interface FileLookupArgs {
  userId: string;
  clientId: string | number;
  fileId: string | number;
}

export const getFile = ({
  userId,
  clientId,
  fileId,
}: FileLookupArgs): ApiPromise<AssetDto> =>
  api.get(`/${userId}/clients/${clientId}/assets/${fileId}`);

export const getFileUsers = ({
  userId,
  clientId,
  fileId,
}: FileLookupArgs): ApiPromise<UserAccessDto[]> =>
  api.get(`/${userId}/clients/${clientId}/assets/${fileId}/users`);

export interface PatchFileUsersArgs {
  currentUser: CurrentUserHolder;
  clientId: string | number;
  fileId: string | number;
  changedUsers: AssetClientUserUpdateRequest[];
}

export const patchFileUsers = ({
  currentUser,
  clientId,
  fileId,
  changedUsers,
}: PatchFileUsersArgs): ApiPromise<UserAccessDto[]> =>
  api.patch(
    `/${currentUser.data.oktaUserId}/clients/${clientId}/assets/${fileId}/users`,
    JSON.stringify(changedUsers)
  );

export const getNewFilesCount = (oktaUserId: string): ApiPromise<number> =>
  api.get(`${oktaUserId}/user-assets/count?viewed.equals=false`);

export const notifyFilesAreViewed = (oktaUserId: string): ApiPromise =>
  api.post(`${oktaUserId}/user-assets/viewed`);

export interface CheckArchivedFileArgs {
  userId: string;
  clientId: string | number;
  search?: string;
}

export const getCheckArchivedFile = ({
  userId,
  clientId,
  search,
}: CheckArchivedFileArgs): ApiPromise<number> =>
  api.get(
    `/${userId}/clients/${clientId}/assets/count?status.equals=ARCHIVED${
      search ? `&q=${search}` : ''
    }`
  );

// Courses
export interface CourseArgs {
  userId: string;
  courseId: string | number;
}

export interface CmsCourseLessonDto {
  id: string | number;
  progress: number;
}

export interface CmsCourseDto {
  id: string | number;
  status?: string;
  lessons: CmsCourseLessonDto[];
}

export const getCourseFullData = ({
  userId,
  courseId,
}: CourseArgs): ApiPromise<CmsCourseDto> =>
  api.get(`${userId}/cms-meta/courses/${courseId}`);

export const getCoursesFullData = ({
  userId,
}: {
  userId: string;
}): ApiPromise<CmsCourseDto[]> => api.get(`${userId}/cms-meta/courses`);

export interface SetLessonProgressArgs {
  userId: string;
  courseId: string | number;
  lessonId: string | number;
  progress: number;
}

export const setLessonProgress = ({
  userId,
  courseId,
  lessonId,
  progress,
}: SetLessonProgressArgs): ApiPromise<CmsCourseLessonDto> =>
  api.put(
    `${userId}/cms-meta/courses/${courseId}/lessons/${lessonId}`,
    JSON.stringify({ progress })
  );

export interface SetCourseStatusArgs {
  userId: string;
  courseId: string | number;
  status: string;
}

export const setCourseStatus = ({
  userId,
  courseId,
  status,
}: SetCourseStatusArgs): ApiPromise<CmsCourseDto> =>
  api.put(`${userId}/cms-meta/courses/${courseId}`, JSON.stringify({ status }));

export const clearCourseMetadata = ({
  userId,
  courseId,
}: CourseArgs): ApiPromise =>
  api.delete(`${userId}/cms-meta/courses/${courseId}`);

export interface DictionaryArgs {
  userId: string;
  type: string;
}

export interface DictionaryEntry {
  name: string;
  description: string;
  [key: string]: unknown;
}

export type DictionaryResponse = DictionaryEntry[];

export const dictionary = ({
  userId,
  type,
}: DictionaryArgs): ApiPromise<DictionaryResponse> =>
  api.get(`${userId}/dictionary/${type}`);

export const lastLoginNotify = (userId: string, data: unknown): ApiPromise =>
  api.put(`${userId}/users`, JSON.stringify(data));

export const logout = (): ApiPromise =>
  api.post(pathWithCurrentUserId('/users/logout'));
